import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const FORM_ID = "db8de6hrh5tg6socjt2g";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db8de6hrh5tg6socjt2g";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "内容聚合",
    desc: "聚合电影、电视剧、动漫等多类别高清影视内容，帮助用户高效发现优质资源。",
  },
  {
    icon: "ri-shield-check-line",
    title: "清晰分类",
    desc: "以规范的分类体系与多维筛选，让浏览与查找都变得简单直观。",
  },
  {
    icon: "ri-heart-3-line",
    title: "用户为先",
    desc: "持续优化浏览与检索体验，让每一位影视爱好者都能轻松找到所爱。",
  },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "关于我们 - 芭乐视频",
    description:
      "了解芭乐视频的平台定位、内容范围与联系方式。芭乐视频致力于为影视爱好者提供清晰、便捷的高清视频内容浏览与发现体验。",
    keywords: "芭乐视频,关于我们,影视平台,联系方式,版权声明",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get("website_alt") || "").trim();
    if (honey) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value as string);
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (!ok) {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : serverMsg || "提交失败，请稍后再试。"
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-8 md:pb-10">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800 font-medium">关于我们</span>
        </nav>

        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-100 text-accent-900 text-xs font-label font-semibold">
            <i className="ri-information-line" /> 关于芭乐视频
          </span>
          <h1 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
            芭乐视频，让好内容被轻松发现
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
            芭乐视频是一个面向广大影视爱好者的高清视频内容聚合与展示平台。我们聚合了电影、电视剧、动漫等多种类别的内容，
            通过清晰的分类导航、灵活的筛选排序与丰富的作品详情，帮助用户高效浏览、筛选并发现心仪的影视资源。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
          {values.map((item) => (
            <div key={item.title} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
              <span className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-xl`} />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h2>
              <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 mt-14 md:mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-3.5 text-sm text-foreground-600">
                <li className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-md bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-mail-line" />
                  </span>
                  service@balevideo.com
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-md bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-phone-line" />
                  </span>
                  400-880-1200
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-md bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-time-line" />
                  </span>
                  服务时间：每日 9:00 - 21:00
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-md bg-primary-100 text-primary-700 shrink-0">
                    <i className="ri-map-pin-line" />
                  </span>
                  线上服务平台
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-6">
              <h2 className="font-heading text-lg font-semibold text-secondary-900">版权声明</h2>
              <p className="mt-3 text-sm text-secondary-800 leading-relaxed">
                本站所展示的影视作品信息均为演示数据，仅用于产品功能展示，不提供实际的视频播放与下载服务。
                如涉及内容调整或合作事宜，欢迎通过左侧联系方式与我们取得联系。
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg bg-background-50 border border-background-200/70 p-6 md:p-8">
              <h2 className="font-heading text-xl font-semibold text-foreground-950">给我们留言</h2>
              <p className="mt-2 text-sm text-foreground-500">
                对平台有建议、合作意向或使用疑问？填写下方表单，我们会尽快回复你。
              </p>

              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="flex flex-col gap-2">
                  <label htmlFor="about-name" className="text-sm font-medium text-foreground-800">
                    姓名 / 称呼
                  </label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="about-email" className="text-sm font-medium text-foreground-800">
                    邮箱
                  </label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="about-message" className="text-sm font-medium text-foreground-800">
                    留言内容
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请输入你想告诉我们的内容（不超过 500 字）"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
                  />
                </div>

                <div className="contact-alt-field">
                  <label htmlFor="about-website">请勿填写此栏</label>
                  <input
                    id="about-website"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" /> 提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" /> 提交留言
                      </>
                    )}
                  </button>

                  {status === "success" ? (
                    <p className="flex items-center gap-2 text-sm text-primary-700">
                      <i className="ri-checkbox-circle-line" /> 提交成功，感谢你的留言，我们会尽快回复！
                    </p>
                  ) : null}
                  {status === "error" ? (
                    <p className="flex items-center gap-2 text-sm text-accent-800">
                      <i className="ri-error-warning-line" /> {formError || "提交失败，请稍后再试。"}
                    </p>
                  ) : null}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}